/**
 * Carga Automática - Gestión de Carpetas
 *
 * CRUD para carpetas de carga automática DICOM (ADMIN/SUPER_ADMIN only)
 * Permite crear, editar y gestionar carpetas monitoreadas
 */

import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  FolderOpen,
  Plus,
  Search,
  Power,
  PowerOff,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
  Check,
  RefreshCw,
  Wifi,
  WifiOff,
  FolderSync,
  Files,
  Network,
  HardDrive,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/presentation/components/ui/Dialog'
import {
  useHotFolders,
  useCreateHotFolder,
  useUpdateHotFolder,
  useDeleteHotFolder,
  useToggleHotFolder,
  useTestConnection,
  useScanHotFolder,
  useSchedulerStatus,
} from '@/application/hot-folders'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import { APP_ROUTES, DOCUMENT_TYPE_LABELS } from '@/config/constants'
import type {
  PublicHotFolder,
  CreateHotFolderData,
  UpdateHotFolderData,
  FolderType,
} from '@/domain/hot-folder/types'
import type { TipoDeDocumentoDeLaCarpeta } from '@/domain/hot-folder/types'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'enabled', label: 'Habilitadas' },
  { value: 'disabled', label: 'Deshabilitadas' },
]

const FOLDER_TYPE_OPTIONS = [
  { value: 'local', label: 'Carpeta Local' },
  { value: 'smb', label: 'Red (SMB)' },
]

const SCAN_INTERVAL_OPTIONS = [
  { value: '30', label: '30 segundos' },
  { value: '60', label: '1 minuto' },
  { value: '120', label: '2 minutos' },
  { value: '300', label: '5 minutos' },
  { value: '600', label: '10 minutos' },
  { value: '900', label: '15 minutos' },
]

const TIPO_DE_DOCUMENTO_AUTOMATICO = 'AUTO'

const DOCUMENT_TYPE_OPTIONS = [
  { value: TIPO_DE_DOCUMENTO_AUTOMATICO, label: 'Automático según la edad' },
  ...Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({ value, label })),
]

// =============================================================================
// Hot Folder Form Modal
// =============================================================================

interface HotFolderFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hotFolder?: PublicHotFolder | null
  onSuccess?: () => void
}

function HotFolderFormModal({
  open,
  onOpenChange,
  hotFolder,
  onSuccess,
}: HotFolderFormModalProps) {
  const isEditing = !!hotFolder

  const [name, setName] = useState('')
  const [folderPath, setFolderPath] = useState('')
  const [folderType, setFolderType] = useState<FolderType>('local')
  const [networkUsername, setNetworkUsername] = useState('')
  const [networkPassword, setNetworkPassword] = useState('')
  const [networkDomain, setNetworkDomain] = useState('')
  const [scanIntervalSeconds, setScanIntervalSeconds] = useState('60')
  const [autoProcess, setAutoProcess] = useState(false)
  const [defaultDocumentType, setDefaultDocumentType] =
    useState<TipoDeDocumentoDeLaCarpeta>(TIPO_DE_DOCUMENTO_AUTOMATICO)
  const [error, setError] = useState<string | null>(null)
  const [connectionTested, setConnectionTested] = useState(false)

  const createMutation = useCreateHotFolder()
  const updateMutation = useUpdateHotFolder()
  const testConnectionMutation = useTestConnection()

  const handleTestConnection = async () => {
    if (!folderPath.trim()) {
      setError('Ingrese la ruta de la carpeta')
      return
    }

    try {
      const result = await testConnectionMutation.mutateAsync({
        folderPath: folderPath.trim(),
        folderType,
        credentials: folderType === 'smb' ? {
          networkUsername: networkUsername || undefined,
          networkPassword: networkPassword || undefined,
          networkDomain: networkDomain || undefined,
        } : undefined,
      })

      if (result.success) {
        toast.success('Conexión exitosa')
        setConnectionTested(true)
        setError(null)
      } else {
        toast.error(result.message || 'Error de conexión')
        setError(result.message || 'No se pudo conectar a la carpeta')
        setConnectionTested(false)
      }
    } catch (err) {
      const message = getErrorMessage(err, 'Error al probar conexión')
      setError(message)
      toast.error(message)
      setConnectionTested(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('El nombre es requerido')
      return
    }

    if (!folderPath.trim()) {
      setError('La ruta de la carpeta es requerida')
      return
    }

    try {
      if (isEditing && hotFolder) {
        const updateData: UpdateHotFolderData = {
          name: name.trim(),
          folderPath: folderPath.trim(),
          folderType,
          scanIntervalSeconds: parseInt(scanIntervalSeconds, 10),
          autoProcess,
          defaultDocumentType,
        }
        if (folderType === 'smb') {
          updateData.networkUsername = networkUsername || undefined
          if (networkPassword) updateData.networkPassword = networkPassword
          updateData.networkDomain = networkDomain || undefined
        }
        await updateMutation.mutateAsync({ id: hotFolder.id, data: updateData })
        toast.success('Carpeta actualizada correctamente')
      } else {
        const createData: CreateHotFolderData = {
          name: name.trim(),
          folderPath: folderPath.trim(),
          folderType,
          scanIntervalSeconds: parseInt(scanIntervalSeconds, 10),
          autoProcess,
          defaultDocumentType,
        }
        if (folderType === 'smb') {
          createData.networkUsername = networkUsername || undefined
          createData.networkPassword = networkPassword || undefined
          createData.networkDomain = networkDomain || undefined
        }
        await createMutation.mutateAsync(createData)
        toast.success('Carpeta creada correctamente')
      }

      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = getErrorMessage(err, 'Error al guardar la carpeta')
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setName('')
    setFolderPath('')
    setFolderType('local')
    setNetworkUsername('')
    setNetworkPassword('')
    setNetworkDomain('')
    setScanIntervalSeconds('60')
    setAutoProcess(false)
    setDefaultDocumentType(TIPO_DE_DOCUMENTO_AUTOMATICO)
    setError(null)
    setConnectionTested(false)
    onOpenChange(false)
  }

  // Reset form when hotFolder changes or modal opens
  useEffect(() => {
    if (open && hotFolder) {
      setName(hotFolder.name)
      setFolderPath(hotFolder.folderPath)
      setFolderType(hotFolder.folderType)
      setScanIntervalSeconds(hotFolder.scanIntervalSeconds.toString())
      setAutoProcess(hotFolder.autoProcess)
      // Note: We don't have full details for editing, only public data
    } else if (open && !hotFolder) {
      // Reset for create mode
      setName('')
      setFolderPath('')
      setFolderType('local')
      setNetworkUsername('')
      setNetworkPassword('')
      setNetworkDomain('')
      setScanIntervalSeconds('60')
      setAutoProcess(false)
      setDefaultDocumentType(TIPO_DE_DOCUMENTO_AUTOMATICO)
    }
    setError(null)
    setConnectionTested(false)
  }, [open, hotFolder])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Carpeta' : 'Nueva Carpeta'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique la configuración de la carpeta'
              : 'Configure una carpeta para detectar archivos DICOM automáticamente'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Rayos X Sala 1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Tipo de Carpeta
            </label>
            <Select
              options={FOLDER_TYPE_OPTIONS}
              value={folderType}
              onChange={(e) => {
                setFolderType(e.target.value as FolderType)
                setConnectionTested(false)
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
              Ruta de la Carpeta <span className="text-red-500">*</span>
            </label>
            <p className="mb-1 text-xs text-[var(--foreground-muted)]">
              Es la ruta <strong>en el servidor donde está instalado JH LUMINA</strong>, no en
              su computador. El equipo de imágenes debe dejar los archivos ahí.
            </p>
            <div className="flex gap-2">
              <Input
                className="flex-1"
                value={folderPath}
                onChange={(e) => {
                  setFolderPath(e.target.value)
                  setConnectionTested(false)
                }}
                placeholder={folderType === 'local' ? '/ruta/a/carpeta' : '\\\\servidor\\compartida'}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={testConnectionMutation.isPending}
              >
                {testConnectionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : connectionTested ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Wifi className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-[var(--foreground-muted)] mt-1">
              {folderType === 'local'
                ? 'Ruta absoluta en el servidor, ej: /home/dicom/incoming'
                : 'Ruta de red Windows, ej: \\\\192.168.1.100\\dicom'}
            </p>
          </div>

          {folderType === 'smb' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                    Usuario
                  </label>
                  <Input
                    value={networkUsername}
                    onChange={(e) => setNetworkUsername(e.target.value)}
                    placeholder="usuario"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                    Dominio
                  </label>
                  <Input
                    value={networkDomain}
                    onChange={(e) => setNetworkDomain(e.target.value)}
                    placeholder="WORKGROUP"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                  Contraseña
                </label>
                <Input
                  type="password"
                  value={networkPassword}
                  onChange={(e) => setNetworkPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Intervalo de Escaneo
              </label>
              <Select
                options={SCAN_INTERVAL_OPTIONS}
                value={scanIntervalSeconds}
                onChange={(e) => setScanIntervalSeconds(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--foreground)] mb-1">
                Tipo Doc. por Defecto
              </label>
              <Select
                options={DOCUMENT_TYPE_OPTIONS}
                value={defaultDocumentType}
                onChange={(e) => setDefaultDocumentType(e.target.value as TipoDeDocumentoDeLaCarpeta)}
              />
              {defaultDocumentType === TIPO_DE_DOCUMENTO_AUTOMATICO && (
                <p className="mt-1 text-xs text-[var(--foreground-muted)]">
                  Registro civil hasta los 7 años, tarjeta de identidad hasta los 18 y
                  cédula desde ahí. Si el equipo no envía la fecha de nacimiento, se
                  registra con cédula.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoProcess"
              checked={autoProcess}
              onChange={(e) => setAutoProcess(e.target.checked)}
              className="rounded border-[var(--border)]"
            />
            <label htmlFor="autoProcess" className="text-sm">
              Procesar automáticamente (crear paciente y estudio sin revisión)
            </label>
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isEditing ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  {isEditing ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Guardar
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Crear
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hotFolder: PublicHotFolder | null
  onConfirm: () => void
  isPending: boolean
}

function DeleteModal({
  open,
  onOpenChange,
  hotFolder,
  onConfirm,
  isPending,
}: DeleteModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader>
          <DialogTitle>Eliminar Carpeta</DialogTitle>
          <DialogDescription>
            ¿Está seguro que desea eliminar la carpeta <strong>{hotFolder?.name}</strong>?
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 p-3 rounded-md bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-400 mt-4">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm">
            Los archivos ya procesados y estudios creados no serán afectados.
          </span>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Eliminando...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Eliminar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export default function HotFoldersPage() {
  const navigate = useNavigate()

  // State
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingHotFolder, setEditingHotFolder] = useState<PublicHotFolder | null>(null)
  const [deletingHotFolder, setDeletingHotFolder] = useState<PublicHotFolder | null>(null)

  // Queries
  const { data: hotFolders, isLoading, refetch } = useHotFolders()
  const { data: schedulerStatus } = useSchedulerStatus()

  const toggleMutation = useToggleHotFolder()
  const deleteMutation = useDeleteHotFolder()
  const scanMutation = useScanHotFolder()

  // Filter hot folders
  const filteredHotFolders = useMemo(() => {
    if (!hotFolders) return []

    let result = [...hotFolders]

    if (search) {
      const searchLower = search.toLowerCase()
      result = result.filter(
        (hf) =>
          hf.name.toLowerCase().includes(searchLower) ||
          hf.folderPath.toLowerCase().includes(searchLower)
      )
    }

    if (statusFilter === 'enabled') {
      result = result.filter((hf) => hf.isEnabled)
    } else if (statusFilter === 'disabled') {
      result = result.filter((hf) => !hf.isEnabled)
    }

    return result
  }, [hotFolders, search, statusFilter])

  // Handlers
  const handleToggle = (hotFolder: PublicHotFolder) => {
    toggleMutation.mutate(hotFolder.id, {
      onSuccess: () => {
        toast.success(hotFolder.isEnabled ? 'Carpeta deshabilitada' : 'Carpeta habilitada')
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al cambiar estado de la carpeta'))
      },
    })
  }

  const handleScan = (hotFolder: PublicHotFolder) => {
    scanMutation.mutate(hotFolder.id, {
      onSuccess: (result) => {
        toast.success(`Escaneo completado: ${result.newFiles} nuevos archivos detectados`)
      },
      onError: (err) => {
        toast.error(getErrorMessage(err, 'Error al escanear carpeta'))
      },
    })
  }

  const handleDelete = () => {
    if (deletingHotFolder) {
      deleteMutation.mutate(deletingHotFolder.id, {
        onSuccess: () => {
          toast.success('Carpeta eliminada correctamente')
          setDeletingHotFolder(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar la carpeta'))
        },
      })
    }
  }

  const handleViewFiles = (hotFolder: PublicHotFolder) => {
    navigate(APP_ROUTES.ADMIN.HOT_FOLDER_FILES(hotFolder.id))
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Carga Automática</h1>
          <p className="text-[var(--foreground-muted)]">
            Carpetas monitoreadas para importar archivos DICOM automáticamente
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refrescar
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva Carpeta
          </Button>
        </div>
      </div>

      {/* Scheduler Status */}
      {schedulerStatus && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {schedulerStatus.isRunning ? (
                  <FolderSync className="h-5 w-5 text-green-500" />
                ) : (
                  <WifiOff className="h-5 w-5 text-red-500" />
                )}
                <span className="font-medium">
                  Scheduler: {schedulerStatus.isRunning ? 'Activo' : 'Detenido'}
                </span>
              </div>
              <Badge variant="secondary">
                {schedulerStatus.activeFolders} carpetas activas
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                placeholder="Buscar por nombre o ruta..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>

            <div className="w-[150px]">
              <Select
                options={STATUS_OPTIONS}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>

            <div className="text-sm text-[var(--foreground-muted)] ml-auto">
              {filteredHotFolders.length} carpetas
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Modo</TableHead>
                  <TableHead>Archivos</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[180px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredHotFolders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <FolderOpen className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                      <p className="mt-2 text-[var(--foreground-muted)]">
                        No hay carpetas configuradas
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredHotFolders.map((hotFolder) => (
                    <TableRow key={hotFolder.id}>
                      <TableCell className="font-medium">
                        {hotFolder.name}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate font-mono text-sm">
                        {hotFolder.folderPath}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {hotFolder.folderType === 'local' ? (
                            <HardDrive className="h-4 w-4" />
                          ) : (
                            <Network className="h-4 w-4" />
                          )}
                          <span>{hotFolder.folderType === 'local' ? 'Local' : 'Red'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={hotFolder.autoProcess ? 'default' : 'secondary'}>
                          {hotFolder.autoProcess ? 'Automático' : 'Manual'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Files className="h-4 w-4 text-[var(--foreground-muted)]" />
                          <span>{hotFolder.fileCount}</span>
                          {hotFolder.pendingCount > 0 && (
                            <Badge variant="warning" className="text-xs">
                              {hotFolder.pendingCount} pendientes
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={hotFolder.isEnabled ? 'success' : 'secondary'}>
                          {hotFolder.isEnabled ? 'Habilitado' : 'Deshabilitado'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewFiles(hotFolder)}
                            title="Ver archivos"
                          >
                            <Files className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleScan(hotFolder)}
                            title="Escanear ahora"
                            disabled={scanMutation.isPending || !hotFolder.isEnabled}
                          >
                            <RefreshCw className={`h-4 w-4 ${scanMutation.isPending ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingHotFolder(hotFolder)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(hotFolder)}
                            title={hotFolder.isEnabled ? 'Deshabilitar' : 'Habilitar'}
                            disabled={toggleMutation.isPending}
                          >
                            {hotFolder.isEnabled ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingHotFolder(hotFolder)}
                            title="Eliminar"
                            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <HotFolderFormModal
        open={showCreateModal || !!editingHotFolder}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateModal(false)
            setEditingHotFolder(null)
          }
        }}
        hotFolder={editingHotFolder}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={!!deletingHotFolder}
        onOpenChange={(open) => !open && setDeletingHotFolder(null)}
        hotFolder={deletingHotFolder}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
